import React from 'react';

import { useHideDiscountFromConsumer } from 'shared/hooks/use-hide-discounts-from-consumer';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useUser from 'src/hooks/use-user';
import useUI from 'src/hooks/use-ui';
import { useRecentlyViewedCarouselEnabled } from 'src/utils/personalization/hooks/use-personalization-enabled';

import { BrandCarousel } from 'src/components/carousels/generic-carousels/brand-carousel';
import { CollectionCarousel } from 'src/components/carousels/products-carousels/collection-carousel';
import { CategoryCarousel } from 'src/components/carousels/generic-carousels/category-carousel';
import { SubcategoryCarousel } from 'src/components/carousels/generic-carousels/subcategory-carousel';
import { SpecialsCarousel, StaffPicksCarousel, TopSellersCarousel } from 'src/components/carousels/generic-carousels';
import { RecentlyViewedCarousel } from 'src/components/carousels/products-carousels';
import { SpecialOffersCarousel } from 'src/components/carousels/offers-carousels';

import { CustomCarousel as UntypedCustomCarousel } from 'src/components/carousels/custom-carousels/custom-carousel';
import { MenuSectionType } from 'src/gql/graphql';
import type { MenuSection } from 'src/gql/graphql';
import { resolveSubcategory, storedProductIds } from './carousel-types';
import type { ProductCarouselType } from './carousel-types';

// `custom-carousel.jsx` is untyped, and `observer()` erases its prop types to `object`, so the
// shape this renderer relies on is declared here rather than inferred from nothing.
const CustomCarousel = (UntypedCustomCarousel as unknown) as React.ComponentType<{
  section: { label: string; products: string[] };
  showTitle?: boolean;
  showLink?: boolean;
}>;

/**
 * Per-type renderers for the Product Carousel block.
 *
 * Only the types whose contents the platform decides are wired up (ECOMM-12683).
 * They need no author configuration beyond the heading, so nothing here reads a
 * type-specific field — that arrives with the category/brand/collection/custom
 * tickets, along with the conditional-field machinery those need.
 *
 * Kept apart from carousel-types.ts so that module stays pure data plus pure
 * functions, testable with no React or hook mocking.
 */

/**
 * What the harness hands a type renderer. `heading` is already resolved (authored
 * beats generated) and interpolated; `undefined` means the author turned the
 * heading off, which is distinct from "there is no heading for this type".
 *
 * `category`, `subcategory`, and `brandId` are present for their respective types;
 * other types receive the default empty string and ignore them. All fields are
 * declared here so the harness can use a single render-call shape regardless
 * of which type is active.
 */
export type ProductCarouselRenderProps = {
  // eslint-disable-next-line react/no-unused-prop-types -- used by CustomRender; renderers that do not need it safely ignore it via their destructuring pattern
  productIds: string[];
  // eslint-disable-next-line react/no-unused-prop-types -- used by BrandRender; renderers that do not need it safely ignore it via their destructuring pattern
  brandId: string;
  // eslint-disable-next-line react/no-unused-prop-types -- used by CategoryRender and SubcategoryRender; renderers that do not need it safely ignore it via their destructuring pattern
  category: string;
  // eslint-disable-next-line react/no-unused-prop-types -- used by CollectionRender; renderers that do not need it safely ignore it via their destructuring pattern
  collectionSlug: string;
  heading: string | undefined;
  // eslint-disable-next-line react/no-unused-prop-types -- used by CollectionRender; renderers that do not need it safely ignore it via their destructuring pattern
  sortBy: string;
  // eslint-disable-next-line react/no-unused-prop-types -- used by SubcategoryRender; renderers that do not need it safely ignore it via their destructuring pattern
  subcategory: string;
};

/**
 * Every carousel below takes the same pair: an optional `title` override and the
 * existing `showTitle` opt-out (`showTitle === false` is what each one already
 * checks). Passing `title` while hiding it is harmless, so both derive from the
 * single resolved heading.
 */
function headingProps(heading: string | undefined): { title: string | undefined; showTitle: boolean } {
  return { title: heading, showTitle: heading !== undefined };
}

function RecentlyViewedRender({ heading }: ProductCarouselRenderProps): JSX.Element {
  const { isLoggedIn } = useUser();
  const { isKiosk } = useUI();
  const personalizationEnabled = useRecentlyViewedCarouselEnabled();
  // Recently Viewed is personal to a signed-in shopper, and a shared kiosk has no
  // meaningful "you". Guards carried over from the standalone RecentlyViewedSection block.
  if (isKiosk || !personalizationEnabled || !isLoggedIn) {
    return <></>;
  }
  return <RecentlyViewedCarousel {...headingProps(heading)} />;
}

function ItemsOnSaleRender({ heading }: ProductCarouselRenderProps): JSX.Element {
  const { dispensary } = useDispensary();
  // Some stores are configured to never show discounts to shoppers; a carousel
  // built entirely from discounted products has to respect that.
  const hideSpecials = useHideDiscountFromConsumer(dispensary);
  if (hideSpecials) {
    return <></>;
  }
  return <SpecialsCarousel {...headingProps(heading)} />;
}

function TopSellersRender({ heading }: ProductCarouselRenderProps): JSX.Element {
  return <TopSellersCarousel {...headingProps(heading)} />;
}

function StaffPicksRender({ heading }: ProductCarouselRenderProps): JSX.Element {
  return <StaffPicksCarousel {...headingProps(heading)} />;
}

function OffersRender({ heading }: ProductCarouselRenderProps): JSX.Element {
  return <SpecialOffersCarousel {...headingProps(heading)} />;
}

function BrandRender({ brandId, heading }: ProductCarouselRenderProps): JSX.Element {
  const { showTitle } = headingProps(heading);
  return <BrandCarousel section={{ label: heading ?? '', brandId }} showTitle={showTitle} showLink={showTitle} />;
}

function CategoryRender({ heading, category }: ProductCarouselRenderProps): JSX.Element {
  const { dispensary } = useDispensary();
  // `dispensaryState` is a two-letter US state code used by checkNeedToMakeEdibleIngestible
  // to relabel edible categories in PA. It is optional — undefined degrades gracefully.
  const dispensaryState = dispensary?.location?.state;
  // CategoryCarousel reads section.label as its carousel title (line 28 in category-carousel.jsx:
  // `title={transformedLabel}`). It does NOT derive its own label — the edible-to-ingestible
  // transformation applies the PA relabel to whatever label we pass in, not a label it computes
  // itself. We pass the resolved heading so an authored heading wins, and the transformation
  // still applies to it. When heading is undefined (showHeading='false'), we pass an empty
  // string — the carousel uses hideTitle (via showTitle=false) to suppress it.
  const { showTitle } = headingProps(heading);
  return (
    <CategoryCarousel
      section={{ category, label: heading ?? '' }}
      dispensaryState={dispensaryState}
      showTitle={showTitle}
    />
  );
}

function SubcategoryRender({ heading, category, subcategory }: ProductCarouselRenderProps): JSX.Element {
  const { dispensary } = useDispensary();
  const dispensaryState = dispensary?.location?.state;
  // Defensively re-resolve the subcategory here so a stale stored value (left over from
  // a different category before the author updated it) can never trigger the wrong query.
  // resolveSubcategory returns '' when the subcategory is not valid for the category.
  const validSubcategory = resolveSubcategory(category, subcategory);
  const { showTitle } = headingProps(heading);
  return (
    <SubcategoryCarousel
      section={{ category, label: heading ?? '', subcategory: validSubcategory }}
      dispensaryState={dispensaryState}
      showTitle={showTitle}
    />
  );
}

/**
 * Delegates wholesale to `CollectionCarousel`, which is the security boundary for exclusive
 * collections and must stay the only implementation of that rule.
 *
 * `useCollections` derives `collectionDisplayMode` from the collection's `isExclusive`, the
 * server's `isAccessibleToCurrentUser`, and the shopper's logged-in state; `CollectionCarousel`
 * then returns `null` for 'hidden' and renders the HighlightCard for 'exclusive'. None of that is
 * re-derived, cached, or short-circuited here, so an authored CMS block cannot become a way to
 * render a gated collection to a shopper who should not see it.
 *
 * `collectionId` receives the stored **slug**: `getCollection` accepts a slug in place of an id,
 * and the slug is the portable identifier across stores. A store with no matching slug resolves no
 * collection and the carousel renders nothing, which is the correct silent-empty outcome.
 */
function CollectionRender({ collectionSlug, heading, sortBy }: ProductCarouselRenderProps): JSX.Element {
  const { showTitle } = headingProps(heading);
  // `CollectionCarousel` is typed for a full `MenuSection` GraphQL entity but only reads
  // `collectionId`, `label`, and `sortBy`. The rest of that "row in the database" shape does not
  // exist for a Puck-authored block, so the cast documents the gap in one place.
  const section = ({ collectionId: collectionSlug, label: heading ?? '', sortBy } as unknown) as MenuSection;
  return <CollectionCarousel section={section} showTitle={showTitle} showLink={showTitle} />;
}

/**
 * Delegates to `CustomCarousel`, which owns the ordering behaviour this type exists for: it fetches
 * the author's ids via `useDispensaryProducts({ filters: { productIds } })` and then re-sorts the
 * results to match the stored id order, because the products query does not honour it.
 *
 * The ids are coerced rather than trusted -- see `storedProductIds`. Passing a non-array through
 * would reach `lodash.findIndex` inside the carousel's sort and produce an order that silently
 * ignores the author's sequence, which is the one thing this type must not do.
 */
function CustomRender({ heading, productIds }: ProductCarouselRenderProps): JSX.Element {
  const { showTitle } = headingProps(heading);
  return (
    <CustomCarousel
      section={{ label: heading ?? '', products: storedProductIds(productIds) }}
      showTitle={showTitle}
      // `showLink` is always false, unlike every sibling renderer. A hand-picked list has no page of
      // its own to link to: `CustomCarousel` derives its "View All" href from
      // `usePaths({ menuSection: { id } })`, and a Puck-authored block has no menu-section id, so the
      // link would resolve to `/collection/undefined` on the published storefront. `BrandCarousel`
      // avoids this by building its href from the brand slug rather than a section id.
      showLink={false}
    />
  );
}

export const PRODUCT_CAROUSEL_RENDERERS: Partial<
  Record<ProductCarouselType, React.ComponentType<ProductCarouselRenderProps>>
> = {
  [MenuSectionType.brand]: BrandRender,
  [MenuSectionType.collection]: CollectionRender,
  [MenuSectionType.custom]: CustomRender,
  [MenuSectionType.category]: CategoryRender,
  [MenuSectionType.subcategory]: SubcategoryRender,
  [MenuSectionType.recentlyViewed]: RecentlyViewedRender,
  [MenuSectionType.specials]: ItemsOnSaleRender,
  [MenuSectionType.topSellers]: TopSellersRender,
  [MenuSectionType.staffPicks]: StaffPicksRender,
  [MenuSectionType.offers]: OffersRender,
};

/**
 * The renderer for a stored `carouselType`, or `undefined` when that type has none
 * yet. Same own-property guard as `carouselTypeDefinition`: saved page data is JSON
 * a hand-edit can author, and a stored `constructor` must resolve to nothing rather
 * than to something off `Object.prototype`.
 */
export function carouselTypeRenderer(
  carouselType: string
): React.ComponentType<ProductCarouselRenderProps> | undefined {
  return Object.prototype.hasOwnProperty.call(PRODUCT_CAROUSEL_RENDERERS, carouselType)
    ? PRODUCT_CAROUSEL_RENDERERS[carouselType as ProductCarouselType]
    : undefined;
}
